import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Tooltip,
  Alert,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { getFileVersions } from "../../services/apiService";
import { formatDate, formatSize, truncateHash } from "./shared/formatters";

const POPPINS = { fontFamily: "'Poppins', sans-serif" };
const HEADER_COLOR = "#1B2559";
const PRIMARY_COLOR = "#1976d2";

/**
 * Right-side Drawer that shows the full version timeline for a SharePoint file.
 *
 * @param {{ open: boolean, file: object|null, onClose: () => void }} props
 */
const FileVersionHistoryDrawer = ({ open, file, onClose }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedHash, setCopiedHash] = useState(null);

  useEffect(() => {
    if (!open || !file?.item_id) return;
    setLoading(true);
    setError(null);
    setVersions([]);

    getFileVersions(file.item_id)
      .then((res) => {
        if (res?.error) {
          setError(res.message || "Failed to load version history.");
          return;
        }
        setVersions(res?.versions ?? []);
      })
      .catch(() => setError("An unexpected error occurred."))
      .finally(() => setLoading(false));
  }, [open, file?.item_id]);

  const handleCopyHash = (hash) => {
    navigator.clipboard.writeText(hash).catch(() => {});
    setCopiedHash(hash);
    setTimeout(() => setCopiedHash(null), 2000);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, p: 0 } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR }}
        >
          Version History
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* File name sub-header */}
      {file && (
        <Box sx={{ px: 2.5, py: 1.5, backgroundColor: "#f5f7fb" }}>
          <Typography
            variant="body2"
            sx={{
              ...POPPINS,
              fontWeight: 600,
              color: PRIMARY_COLOR,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={file.name}
          >
            {file.name}
          </Typography>
        </Box>
      )}

      <Divider />

      {/* Body */}
      <Box sx={{ px: 2.5, py: 2, overflowY: "auto", flex: 1 }}>
        {loading && (
          <Box>
            {Array.from({ length: 3 }).map((_, i) => (
              <Box key={i} sx={{ mb: 3 }}>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="55%" />
              </Box>
            ))}
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ ...POPPINS }}>{error}</Alert>
        )}

        {!loading && !error && versions.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={POPPINS}>
            No version history available.
          </Typography>
        )}

        {!loading && !error && versions.length > 0 && (
          <Stepper orientation="vertical" nonLinear>
            {versions.map((v, idx) => (
              <Step key={`${v.version_number}-${idx}`} active expanded>
                <StepLabel
                  StepIconComponent={() =>
                    v.is_current ? (
                      <CheckCircleIcon sx={{ color: PRIMARY_COLOR, fontSize: 20 }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
                    )
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR }}
                    >
                      Version {v.version_number ?? idx + 1}
                    </Typography>
                    {v.is_current && (
                      <Chip
                        label="Current"
                        size="small"
                        color="primary"
                        sx={{ ...POPPINS, fontSize: "0.65rem", height: 18 }}
                      />
                    )}
                  </Box>
                </StepLabel>

                <StepContent>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={POPPINS}>
                      <b>Ingested:</b> {formatDate(v.scanned_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={POPPINS}>
                      <b>Size:</b> {formatSize(v.size_bytes)}
                    </Typography>
                    {v.content_hash && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={POPPINS}>
                          <b>Hash:</b> <Tooltip title={v.content_hash}>{truncateHash(v.content_hash)}</Tooltip>
                        </Typography>
                        <Tooltip
                          title={copiedHash === v.content_hash ? "Copied!" : "Copy full hash"}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleCopyHash(v.content_hash)}
                            sx={{ p: 0.25, color: "#9e9e9e" }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    {v.job_run_id && (
                      <Typography variant="caption" color="text.secondary" sx={POPPINS}>
                        <b>Job Run:</b> {v.job_run_id}
                      </Typography>
                    )}
                    {v.is_current && (
                      <Box sx={{ mt: 1 }}>
                        {/* <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            reprocessing ? (
                              <CircularProgress size={12} />
                            ) : (
                              <ReplayIcon fontSize="small" />
                            )
                          }
                          disabled={reprocessing}
                          onClick={handleReprocess}
                          sx={{ ...POPPINS, fontSize: "0.7rem", textTransform: "none", py: 0.25 }}
                        >
                          {reprocessing ? "Reprocessing…" : "Reprocess"}
                        </Button> */}
                      </Box>
                    )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        )}
      </Box>
    </Drawer>
  );
};

FileVersionHistoryDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  file: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default FileVersionHistoryDrawer;
